<script>
import { computed, ref, watch } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import useRaf from '@monogrid/vue3-boost/functions/useRaf'
import { useElasticNumber } from '@/@monogrid/vue3-boost/functions'

export default {
  name: 'CircleBtn',
  inheritAttrs: false,
  props: {
    to: { type: Object, default: undefined },
    type: { type: String, default: undefined },
    loading: { type: Boolean, default: false },
    dimension: { type: String, default: 'auto' }
  },
  setup (props, { attrs }) {
    const canvas = ref()
    const content = ref()
    const circlePoints = 60
    const center = computed(() => canvasHeight.value ? canvasHeight.value / 2 : 0)
    const offsetFactor = useElasticNumber(0.5)
    const radius = useElasticNumber(0)
    const dimensionFactor = 1.5
    let time = 0
    let ctx

    const circle = (definition, index, offset, freq, displace = Math.PI * 2 * Math.random()) => {
      const step = (Math.PI * 2) / definition
      const startAngle = (Math.PI * 2) * Math.random()

      return (pos) => {
        ctx.beginPath()
        const points = []
        let i
        for (i = 0; i <= definition; i++) {
          const angle = startAngle + step * i % Math.PI * 2
          const r = radius.value + index * 3 + (offset * Math.sin(pos + freq * angle) * Math.cos(displace + angle + pos)) * offsetFactor.value

          const x = center.value + r * Math.cos(angle)
          const y = center.value + r * Math.sin(angle)
          points.push({ x, y })
        }
        // move to the first point
        ctx.moveTo(points[0].x, points[0].y)
        for (i = 1; i < points.length - 2; i++) {
          const xc = (points[i].x + points[i + 1].x) / 2
          const yc = (points[i].y + points[i + 1].y) / 2
          ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc)
        }
        // curve through the last two points
        ctx.quadraticCurveTo(points[i].x, points[i].y, points[0].x, points[0].y)
        ctx.stroke()
      }
    }
    const circles = []
    const circleConfigs = props.dimension === 'auto'
      ? [
          [5, 10],
          [12, 6],
          [6, 12]
        ]
      : [
          [2, 4]
        ]
    circleConfigs.forEach((config, i) => {
      circles.push(circle(circlePoints, i + 1, ...config))
    })

    const componentTag = computed(() => {
      if (props.to) {
        return 'router-link'
      } else if (attrs.href) {
        return 'a'
      }
      return 'button'
    })

    const height = computed(() => {
      return content.value?.getBoundingClientRect().width + (content.value?.getBoundingClientRect().width / 3)
    })

    const canvasHeight = computed(() => {
      return height.value * dimensionFactor
    })

    const onRaf = (delta, timestamp) => {
      offsetFactor.update(delta)
      radius.update(delta)

      ctx.clearRect(0, 0, canvasHeight.value, canvasHeight.value)
      time += delta
      const wavePos = time * Math.PI
      ctx.beginPath()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 3
      ctx.arc(center.value, center.value, radius.value, 0, Math.PI * 2)
      ctx.stroke()
      ctx.fillStyle = '#19181a'
      ctx.fill()
      ctx.lineWidth = 0.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      circles.forEach(circle => circle(wavePos))
    }

    const { toggle: toggleRaf } = useRaf(onRaf)

    const enterCircle = () => {
      offsetFactor.target = 1
      radius.target = center.value * 0.8
      content.value.classList.remove('zoom-out')
      content.value.classList.add('zoom-in')
      if (content.value.querySelectorAll('.arrow')) {
        const arrow = content.value.querySelectorAll('.arrow')[0]
        arrow.classList.remove('zoom-out--arrow')
        arrow.classList.add('zoom-in--arrow')
      }
    }
    const leaveCircle = () => {
      offsetFactor.target = 0.5
      radius.target = center.value * 0.6
      content.value.classList.remove('zoom-in')
      content.value.classList.add('zoom-out')
      if (content.value.querySelectorAll('.arrow')) {
        const arrow = content.value.querySelectorAll('.arrow')[0]
        arrow.classList.remove('zoom-in--arrow')
        arrow.classList.add('zoom-out--arrow')
      }
    }

    watch(canvas, () => {
      if (!canvas.value || ctx) return
      ctx = canvas.value.getContext('2d')
      canvas.value.width = canvasHeight.value
      canvas.value.height = canvasHeight.value
      canvas.value.style.width = canvasHeight.value + 'px'
      canvas.value.style.height = canvasHeight.value + 'px'
      toggleRaf(true)
    })

    watch(center, () => {
      if (!center.value) return
      radius.target = center.value * 0.6
    })

    useIntersectionObserver(
      canvas,
      ([{ isIntersecting }], observerElement) => {
        toggleRaf(isIntersecting)
      }
    )

    return {
      canvas,
      content,
      height,
      componentTag,
      enterCircle,
      leaveCircle
    }
  }
}
</script>

<template>
  <component
    v-bind="$attrs"
    :is="componentTag"
    class="cta inline-block flex justify-center items-center"
    :class="{loading}"
    :type="to || $attrs.href ? undefined : (type || 'button')"
    :to="to"
    data-cursor="hide"
    @mouseenter="enterCircle"
    @mouseleave="leaveCircle"
  >
    <canvas ref="canvas" />
    <span
      ref="content"
      class="cta-label relative flex items-center t-l font-bold px-4 flex-col justify-center"
      :style="{height: `${height}px`}"
    >
      <slot />
    </span>
  </component>
</template>

<style lang="scss" scoped>
canvas {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.cta-label.zoom-in {
  transform: scale(1.2);
  transition: transform 0.4s linear;
}

.cta-label.zoom-out {
  transform: scale(1);
  transition: transform 0.4s linear;
}

</style>
